import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "../Signup/SignUp.module.css";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { notify } from "../toast";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { validate } from "../../../utils/validate";
import { sendEmailVerification, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { auth, firebaseEmailContinueUrl, hasFirebaseConfig } from "../../../config/firebase";
import {
  isLocalAuthEnabled,
  LOCAL_DEV_EMAIL,
  LOCAL_DEV_PASSWORD,
  signInLocal,
  signInLocalDemo,
} from "../../../config/localAuth";
import AuthShell from "../AuthShell";

const Login = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const localAuthOn = isLocalAuthEnabled();
  const [data, setData] = useState({
    email: localAuthOn ? LOCAL_DEV_EMAIL : "",
    password: localAuthOn ? LOCAL_DEV_PASSWORD : "",
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [dirty, setDirty] = useState({});

  useEffect(() => {
    setErrors(validate(data, "login"));
  }, [data]);

  const getFirebaseLoginError = (errorCode) => {
    switch (errorCode) {
      case "auth/invalid-email":
        return t("auth.invalidEmail");
      case "auth/user-disabled":
        return t("auth.userDisabled");
      case "auth/invalid-credential":
      case "auth/user-not-found":
      case "auth/wrong-password":
        return t("auth.wrongCredentials");
      case "auth/network-request-failed":
        return t("auth.networkError");
      default:
        return t("auth.signInError");
    }
  };

  const changeHandler = (event) => {
    if (event.target.name === "IsAccepted") {
      setData({ ...data, [event.target.name]: event.target.checked });
    } else {
      setData({ ...data, [event.target.name]: event.target.value });
      setDirty((prev) => ({
        ...prev,
        [event.target.name]: Boolean(prev[event.target.name]) || event.target.value.trim().length > 0,
      }));
    }
  };

  const blurHandler = (event) => {
    if (dirty[event.target.name]) {
      setTouched((prev) => ({ ...prev, [event.target.name]: true }));
    }
  };

  const finishLocalLogin = () => {
    notify(t("auth.loginSuccess"), "success");
    navigate("/", { replace: true });
  };

  const handleLocalDemoLogin = (event) => {
    event.preventDefault();
    if (signInLocalDemo()) {
      finishLocalLogin();
      return;
    }
    notify(t("auth.signInError"), "error");
  };

  const submitHandler = (event) => {
    event.preventDefault();

    // Prefer local demo auth in development, even if validation state is stale.
    if (signInLocal(data.email, data.password)) {
      finishLocalLogin();
      return;
    }

    if (Object.keys(errors).length) {
      notify(t("auth.checkEmailPassword"), "error");
      setTouched({ email: true, password: true });
      return;
    }

    const loginUser = async () => {
      if (!hasFirebaseConfig || !auth) {
        notify(
          localAuthOn ? t("auth.wrongCredentials") : t("auth.firebaseNotConfigured"),
          "error"
        );
        return;
      }

      try {
        const credential = await signInWithEmailAndPassword(
          auth,
          data.email.toLowerCase(),
          data.password
        );
        if (!credential.user.emailVerified) {
          await sendEmailVerification(credential.user, {
            url: firebaseEmailContinueUrl,
          });
          await signOut(auth);
          notify(t("auth.verifyEmailFirst"), "warning");
          return;
        }
        notify(t("auth.loginSuccess"), "success");
        navigate("/", { replace: true });
      } catch (error) {
        notify(getFirebaseLoginError(error?.code), "error");
      }
    };

    loginUser();
  };

  const handleOnClick = () => {
    navigate("/");
  };

  return (
    <AuthShell subtitle={t("auth.brandPanelLogin")}>
      <form className={styles.formLogin} onSubmit={submitHandler} autoComplete="off">
        <h2>{t("auth.signIn")}</h2>
        <p className={styles.formLead}>{t("auth.loginLead")}</p>
        {localAuthOn && (
          <div className={styles.localHint}>
            <p>
              Local only: <code>{LOCAL_DEV_EMAIL}</code> / <code>{LOCAL_DEV_PASSWORD}</code>
            </p>
            <button type="button" className={styles.localDemoBtn} onClick={handleLocalDemoLogin}>
              Continue as local user
            </button>
          </div>
        )}
        <div>
          <label className={styles.fieldLabel} htmlFor="login-email">
            {t("auth.email")}
          </label>
          <div
            className={
              errors.email && touched.email
                ? styles.unCompleted
                : !errors.email && touched.email
                  ? styles.completed
                  : undefined
            }
          >
            <input
              id="login-email"
              type="email"
              name="email"
              value={data.email}
              placeholder={t("auth.email")}
              onChange={changeHandler}
              onBlur={blurHandler}
              autoComplete="off"
            />
            <img src={process.env.PUBLIC_URL + "/img/auth/email.svg"} alt="" />
          </div>
          {errors.email && touched.email && <span className={styles.error}>{errors.email}</span>}
        </div>
        <div>
          <label className={styles.fieldLabel} htmlFor="login-password">
            {t("auth.password")}
          </label>
          <div
            className={
              errors.password && touched.password
                ? styles.unCompleted
                : !errors.password && touched.password
                  ? styles.completed
                  : undefined
            }
          >
            <input
              id="login-password"
              type="password"
              name="password"
              value={data.password}
              placeholder={t("auth.password")}
              onChange={changeHandler}
              onBlur={blurHandler}
              autoComplete="off"
            />
            <img src={process.env.PUBLIC_URL + "/img/auth/password.svg"} alt="" />
          </div>
          {errors.password && touched.password && (
            <span className={styles.error}>{errors.password}</span>
          )}
        </div>

        <div>
          <div className={styles.actions}>
            <button className={styles.primaryBtn} type="submit">
              {t("auth.login")}
            </button>
            <button className={styles.secondaryBtn} type="button" onClick={handleOnClick}>
              {t("auth.cancel")}
            </button>
          </div>
          <p className={styles.footerLink}>
            {t("auth.dontHaveAccount")} <Link to="/signup">{t("auth.createAccountLink")}</Link>
          </p>
        </div>
      </form>
      <ToastContainer />
    </AuthShell>
  );
};

export default Login;
